import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { useIntl } from "react-intl";
import { Calendar, CalendarProps } from "./calendar";
import { DateRange } from "react-day-picker";
import { assertNever } from "@/utils/helpers";
import { Input } from "./input";
import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { cn } from "@/utils/tailwind";
import { useMediaQuery } from "@/utils/hooks";
import { NaiveDate } from "@/utils/date";

export interface NaiveDateRange {
  from?: NaiveDate;
  to?: NaiveDate;
}

interface BaseDatePickerProps extends Omit<CalendarProps, "mode"> {
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  disabled?: boolean;
  openOnFocus?: boolean;
  onFocus?: () => void;
  onBlur: () => void;
}

interface DatePickerPopoverProps extends BaseDatePickerProps {
  formattedValue: string;
  children: React.ReactNode;
}

const DatePickerPopover = React.forwardRef<
  HTMLInputElement,
  DatePickerPopoverProps
>(
  (
    {
      formattedValue,
      autoFocus,
      className,
      placeholder,
      name,
      id,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      startDecorator,
      endDecorator,
      disabled,
      openOnFocus: defaultOpenOnFocus = false,
      onFocus,
      onBlur,
      children,
    },
    forwardedRef,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpen] = React.useState(autoFocus);
    const [openOnFocus, setOpenOnFocus] = React.useState(defaultOpenOnFocus);
    return (
      <>
        <Popover
          open={disabled ? false : open}
          onOpenChange={(open) => {
            setOpen(open);
            if (open) {
              onFocus && onFocus();
            } else {
              onBlur && onBlur();
            }
          }}
        >
          <PopoverTrigger asChild>
            <Input
              type="text"
              readOnly
              disabled={disabled}
              startDecorator={startDecorator}
              endDecorator={endDecorator}
              name={name}
              id={id}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              className={cn(
                "hover:cursor-pointer selection:bg-white",
                className,
              )}
              inputProps={{ className: "pointer-events-none" }}
              placeholder={placeholder}
              value={formattedValue}
              onKeyDown={(event) => {
                if (event.key == "Enter") {
                  event.preventDefault();
                  setOpen(true);
                }
              }}
              onFocus={() => {
                if (openOnFocus) {
                  setOpen(true);
                }
              }}
              onBlur={(event) => {
                if (!contentRef.current?.contains(event.relatedTarget)) {
                  setOpenOnFocus(defaultOpenOnFocus);
                }
              }}
              onChange={() => {}}
              ref={inputRef}
            />
          </PopoverTrigger>
          <PopoverContent
            className="w-auto"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              setOpenOnFocus(false);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }}
            ref={contentRef}
          >
            {children}
          </PopoverContent>
        </Popover>
      </>
    );
  },
);

interface DatePickerSingleProps extends BaseDatePickerProps {
  value?: NaiveDate;
  onChange?: (date: NaiveDate | null) => void;
}

const DatePickerSingle = React.forwardRef<
  HTMLInputElement,
  DatePickerSingleProps
>(({ value, onChange, defaultMonth: overrideDefaultMonth, ...rest }, ref) => {
  const intl = useIntl();
  const defaultMonth = overrideDefaultMonth || value?.toLocale();
  return (
    <DatePickerPopover
      formattedValue={intl.formatDate(value?.toLocale() || undefined)}
      ref={ref}
      {...rest}
    >
      <Calendar
        mode="single"
        autoFocus
        showOutsideDays={false}
        selected={value?.toLocale()}
        onSelect={(date?: Date) =>
          onChange && onChange((date && NaiveDate.fromDate(date)) || null)
        }
        defaultMonth={defaultMonth}
        {...rest}
      />
    </DatePickerPopover>
  );
});

interface DatePickerRangeProps extends BaseDatePickerProps {
  value?: NaiveDateRange;
  onChange?: (range: NaiveDateRange | null) => void;
}

const DatePickerRange = React.forwardRef<
  HTMLInputElement,
  DatePickerRangeProps
>(({ value, onChange, defaultMonth: overrideDefaultMonth, ...rest }, ref) => {
  const intl = useIntl();
  const defaultMonth = overrideDefaultMonth || value?.from?.toLocale();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  return (
    <DatePickerPopover
      formattedValue={
        value?.from
          ? value?.to
            ? intl.formatDateTimeRange(
                value.from.toLocale(),
                value.to.toLocale(),
              )
            : intl.formatDate(value.from.toLocale())
          : ""
      }
      ref={ref}
      {...rest}
    >
      <Calendar
        mode="range"
        autoFocus
        showOutsideDays={false}
        selected={{
          from: value?.from?.toLocale(),
          to: value?.to?.toLocale(),
        }}
        onSelect={(selected?: DateRange) =>
          onChange &&
          onChange(
            (selected && {
              from: selected.from && NaiveDate.fromDate(selected.from),
              to: selected.to && NaiveDate.fromDate(selected.to),
            }) ||
              null,
          )
        }
        numberOfMonths={isDesktop ? 2 : 1}
        defaultMonth={defaultMonth}
        {...rest}
      />
    </DatePickerPopover>
  );
});

interface SingleDatePickerProps extends DatePickerSingleProps {
  mode: "single";
}

interface RangeDatePickerProps extends DatePickerRangeProps {
  mode: "range";
}

type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (props, ref) => {
    const { mode, ...rest } = props;
    switch (mode) {
      case "single":
        return (
          <DatePickerSingle {...(rest as SingleDatePickerProps)} ref={ref} />
        );
      case "range":
        return (
          <DatePickerRange {...(rest as RangeDatePickerProps)} ref={ref} />
        );
      default:
        assertNever(mode);
    }
  },
);
